import React, { useState, useEffect, Fragment } from "react"
import throttle from "lodash/throttle"
import md5 from "js-md5"
import StickyBox from "react-sticky-box"

import CartContainer from "../CartContainer"
import CartSummaryModal from "../CartSummaryModal"
import FeaturedSection from "./FeaturedSection"
import isVisibleOnScreen from "../utils/isVisibleOnScreen"
import AddServiceModal from "./components/modal/AddServiceModal"
import ServiceSectionsList from "./components/ServiceSectionsList"
import CartStateStore, {
  ADD_TO_CART,
  CHANGE_STORE,
  getCart,
  useCartStateStore,
} from "../stores/CartStateStore"

import style from "./index.module.scss"
import { logAddToCart } from "../apis/Analytics/gtmActions"
import { registerAddToCartToFacebook } from "../apis/Analytics/registerOrderToFacebook"
import LazyLoadingServiceSection from "./components/LazyLoadingServiceSection";

// ----------------------------------------------------------------------------

function getInitialState() {
  return {
    service: {
      name: "",
      description: "",
    },
    serviceSectionPartConfigs: [],
    showModal: false,
  }
}

function _VenueServicesList(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    venue,
    deliveryAddress,
    selectedServiceId = undefined,
    cashSettings = {},
  } = props

  const max_cash_amount_cents = cashSettings?.max_cash_amount_cents

  // The type of the service sections view style (FLAT or GRID)
  const isSectionsGridView = venue.catalog_view_type !== "FLAT"

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [serviceState, setServiceState] = useState(getInitialState())
  const [currentSectionId, setCurrentSectionId] = useState(null)
  const [cartState, dispatchCartState] = useCartStateStore()

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    dispatchCartState({ type: CHANGE_STORE, payload: venue })

    if (!venue.ordering_enabled) {
      alert(I18n.t("service_ordering.sorry_restaurant_unavailable"));
    }

    if (window) {
      if (!isSectionsGridView) {
        setupMenuHeaderBehaviour()
      }

      setupCurrentService()
    }
  }, [])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function setupMenuHeaderBehaviour() {
    // configures menu header
    // TODO: refactor into a Pure React component (now it's JQuery magic)
    // changes highlighted element while scrolling
    // handle onCLick on menuHeader item, scrolling to corresp section
    $.fn.isVisibleOnScreen = isVisibleOnScreen

    let lastScrollPos = 0
    const scrollWidth = $(".make-scroll").width()
    const $sectionElements = $(".service-section")

    const makeScroll = ($scrollElement, shift) => {
      var xPos = $scrollElement.scrollLeft()

      $scrollElement.animate({
        scrollLeft: xPos + shift,
      })
    }

    const updateCurrentItem = function () {
      const visibleSections = []
      var currentPos = $(this).scrollTop()

      if (lastScrollPos === currentPos) {
        return
      }

      $sectionElements.each(function (i) {
        if ($(this).isVisibleOnScreen()) {
          visibleSections.push($(this))
        }
      })

      if (visibleSections[0]) {
        const sectionId = visibleSections[0].attr("id")
        const $currentItem = $(`*[data-id="${sectionId}"]`)

        $currentItem.parents().siblings().removeClass("active")
        $currentItem.parent().addClass("active")
        if (
          (lastScrollPos) < currentPos &&
          ($currentItem.offset().left > scrollWidth - 250)) {
          makeScroll($(".make-scroll"), $currentItem.offset().left)
        }
        if ((lastScrollPos > currentPos) &&
          ($currentItem.offset().left < 250)) {
          makeScroll($(".make-scroll"), -$(".make-scroll").width())
        }
        lastScrollPos = currentPos
      }
    }

    $(".list-section-title a").on("click", function (e) {
      e.preventDefault()

      if ($(this).parent().hasClass("active")) {
        return
      }
      const $scrollElement = $(".make-scroll")
      const currentHash = $(this).prop("hash")

      $(this).parents().siblings().removeClass("active")
      $(this).parent().addClass("active")

      $("html, body").animate(
        {
          scrollTop: $(currentHash).offset().top - 103,
        },
        200
      )

      if (e.pageX > scrollWidth - 250) {
        makeScroll($scrollElement, $(this).parent().outerWidth())
      }
      if (e.pageX < 250) {
        makeScroll($scrollElement, -250)
      }
    })

    $(window).on("scroll", throttle(updateCurrentItem, 250))
  }

  function setupCurrentService() {
    if (selectedServiceId) {
      let sections = getAvailableSections()
      let service = undefined
      let serviceSection = undefined
      for (const section of sections) {
        service = section.services.find((s) => s.id === selectedServiceId)
        serviceSection = section
        if (service) {
          break
        }
      }
      let configParts = serviceSection.service_section_part_configs

      if (service && serviceSection) {
        handleShowModal(service, configParts)
      }
    }
  }

  function getAvailableSections() {
    const today = new Date().getDay().toString()

    return venue.service_sections.filter((section) => {
      const unavailable_days = section.unavailable_days || []

      return unavailable_days.indexOf(today) === -1
    })
  }

  function handleShowModal(service, serviceSectionPartConfigs) {
    setServiceState({
      service: service,
      serviceSectionPartConfigs: serviceSectionPartConfigs,
      showModal: true,
    })
  }

  function handleSectionBoxPress(sectionId) {
    setCurrentSectionId(sectionId);
  }

  function handleModalHide() {
    setServiceState(getInitialState())
  }

  function handleAddToCart(additionalParts, quantity, specialInstructions) {
    if (!venue.ordering_enabled) {
      alert(I18n.t("service_ordering.cannot_add_to_cart"))
      return
    }

    let sku = serviceState.service.id
    const name = serviceState.service.name
    const section_title = serviceState.service.section_title
    let total_price = serviceState.service.price_cents

    if (additionalParts) {
      additionalParts.forEach((part) => {
        sku += "-" + part.id
        total_price += part.price
      })
    }

    sku += specialInstructions || ""
    sku = md5(sku)

    const update = {
      id: serviceState.service.id,
      name: name,
      quantity: 1,
      original_price_cents: serviceState.service.original_price_cents,
      price_cents: serviceState.service.price_cents,
      additional_parts: additionalParts,
      section_title: section_title,
      special_instructions: specialInstructions,
    }

    dispatchCartState({
      type: ADD_TO_CART,
      payload: { sku: sku, update: update, quantity: quantity },
    })

    // Logs add_to_cart events only when items are inserted the first time in the cart.
    // Does not log items quantity updates
    logAddToCart(update, quantity || 1)
    registerAddToCartToFacebook((update.price_cents / 100) * (quantity || 1))

    setServiceState({ ...serviceState, showModal: false })
  }

  // ----------------------------------------------------------------------------
  // Render Functions
  // ----------------------------------------------------------------------------

  function renderSectionsNavHeader() {

    //Hide the sections flat list
    if (isSectionsGridView && !currentSectionId) {
      return null
    }

    const showBackButton = !!isSectionsGridView && !!currentSectionId;

    return (
      <div className="container-fluid white-background serving-categories-container m-0 p-0">
        <div className="row m-0 p-0">
          <div className="container">
            <div className="row make-scroll">
              <ul className="nav nav-tabs nav-tabs--plain">

                {showBackButton && <li onClick={() => setCurrentSectionId(null) } className="list-section-title">
                  <a><i className="fas fa-long-arrow-left"></i></a>
                </li>}

                {(venue?.service_sections || []).map((serviceSection) => {
                  const { id, title } = serviceSection

                  return (
                    <li onClick={() => handleSectionBoxPress(id)} key={id} className={`list-section-title ${id === currentSectionId ? 'active' : ''}`}>
                      <a
                        data-id={id}
                        href={`#${id}`}
                      >
                        {title}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render the main content with the products
  // It could render in 2 ways:
  // 1. If the isSectionsGridView is TRUE and a section is selected -> lazy load the selected section and render all the corresponding services
  // 2. Otherwise render all the sections (including the services) in the same page
  function renderSectionsContent() {
    if (isSectionsGridView && currentSectionId) {
      return <LazyLoadingServiceSection orderingEnabled={venue.ordering_enabled}
                                                      onShowModal={handleShowModal}
                                                      sectionId={currentSectionId} />
    }

    return  <ServiceSectionsList
      onSectionBoxPress={handleSectionBoxPress}
      flatList={!isSectionsGridView}
      sections={getAvailableSections()}
      onShowModal={handleShowModal}
      orderingEnabled={venue.ordering_enabled}
    />

  }



  function renderCart() {
    return (
      <Fragment>
        <h3 className={style.cartTitle}>{I18n.t("cart")}</h3>
        <CartContainer
          storeId={venue.id}
          visible={cartState.visible}
          dispatchCartState={dispatchCartState}
          cartState={cartState}
          orderingEnabled={venue.ordering_enabled}
          latitude={deliveryAddress.latitude}
          longitude={deliveryAddress.longitude}
          minGoodsPrice={venue.min_goods_price_cents}
          maxCashAmountCents={max_cash_amount_cents}
        />
      </Fragment>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------
  const featuredServices = []
  venue.service_sections?.forEach((section) => {
    section?.services?.forEach((service) => {
      if (!!service.featured) {
        featuredServices.push(service)
      }
    })
  })

  // -------------------------------------

  return (
    <Fragment>
      {featuredServices && featuredServices.length > 0 && (
        <div className="m-0">
          <FeaturedSection
            services={featuredServices}
            sections={venue.service_sections}
            onShowModal={handleShowModal}
            promotion={getCart(cartState).promotion}
          />
        </div>
      )}

      {renderSectionsNavHeader()}

      <div className="venue-services-content container-fluid grey-background">
        <div id="venue-menu" className="container">
          <div className="row">
            <div className="col-sm-12 col-md-7">
              <AddServiceModal
                key={serviceState.service.id}
                show={serviceState.showModal}
                onHide={handleModalHide}
                addToCart={handleAddToCart}
                service={serviceState.service}
                venueType={venue.venue_type}
                serviceSectionPartConfigs={serviceState.serviceSectionPartConfigs}
              />

              {renderSectionsContent()}
            </div>
            <StickyBox offsetTop={0} className="hidden-sm hidden-xs col-md-5">
              {renderCart()}
            </StickyBox>
          </div>
        </div>

        <CartSummaryModal
          dispatchCartState={dispatchCartState}
          cartState={cartState}
          orderingEnabled={venue.ordering_enabled}
          latitude={deliveryAddress.latitude}
          longitude={deliveryAddress.longitude}
          minGoodsPrice={venue.min_goods_price_cents}
          maxCashAmountCents={max_cash_amount_cents}
        />
      </div>
    </Fragment>
  )
}

// ----------------------------------------------------------------------------

export default function VenueServicesList(props) {
  return (
    <CartStateStore>
      <_VenueServicesList {...props}></_VenueServicesList>
    </CartStateStore>
  )
}
